// AGC: Audio Note Modal - Simple audio recording for lot notes
// AGC: Uses MediaRecorder API directly (not voice command hook)

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { X, Mic, Square, Play, Pause, Check, Loader2 } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { useNoteMetadata } from '../../hooks/useNoteMetadata'
import { NoteMetadataStrip } from './NoteMetadataStrip'
import type { GPSLocation } from '../../types/lotNotes'
import toast from 'react-hot-toast'

interface AudioNoteModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (audio: Blob, duration: number, gpsLocation?: GPSLocation, description?: string, taggedUserIds?: string[]) => void
  lotId: string
}

export const AudioNoteModal: React.FC<AudioNoteModalProps> = ({
  isOpen,
  onClose,
  onSave,
  lotId,
}) => {
  const { t } = useTranslation()
  const [isRecording, setIsRecording] = useState(false)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [duration, setDuration] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // AGC: Shared metadata hook — GPS, description, team tags, speech-to-text
  const metadata = useNoteMetadata({ isActive: isOpen })

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)

  // AGC: Cleanup on close
  useEffect(() => {
    return () => { cleanup() }
  }, [isOpen])

  const cleanup = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }
  }, [])

  const handleStartRecording = async () => {
    try {
      setError(null)
      setAudioBlob(null)
      setAudioUrl(null)
      setDuration(0)
      chunksRef.current = []

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : 'audio/webm'

      const recorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = recorder

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType })
        setAudioBlob(blob)
        setAudioUrl(URL.createObjectURL(blob))
        // AGC: Stop all tracks
        stream.getTracks().forEach(t => t.stop())
        streamRef.current = null
      }

      recorder.start(250) // AGC: Collect data every 250ms
      setIsRecording(true)

      // AGC: Duration timer
      const startTime = Date.now()
      timerRef.current = setInterval(() => {
        setDuration(Math.floor((Date.now() - startTime) / 1000))
      }, 500)

    } catch (err: any) {
      console.error('AGC: Failed to start recording', err)
      setError('Não foi possível acessar o microfone. Verifique as permissões.')
      toast.error('Microfone não disponível')
    }
  }

  const handleStopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    setIsRecording(false)
  }

  const togglePlayback = () => {
    if (!audioUrl) return

    if (isPlaying && audioRef.current) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else {
      const audio = new Audio(audioUrl)
      audio.onended = () => setIsPlaying(false)
      audioRef.current = audio
      audio.play()
      setIsPlaying(true)
    }
  }

  const handleReRecord = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }
    setAudioBlob(null)
    setAudioUrl(null)
    setDuration(0)
    setIsPlaying(false)
  }

  const handleSave = () => {
    if (!audioBlob || audioBlob.size < 100) {
      toast.error('Nenhuma gravação válida. Grave novamente.')
      return
    }
    onSave(audioBlob, duration, metadata.gpsLocation, metadata.description, metadata.taggedUserIds)
    toast.success('Áudio salvo!')
    handleClose()
  }

  const handleClose = () => {
    cleanup()
    setIsRecording(false)
    setAudioBlob(null)
    setAudioUrl(null)
    setDuration(0)
    setIsPlaying(false)
    setError(null)
    metadata.reset()
    onClose()
  }

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md">
        {/* AGC: Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('notes.recordAudio', 'Gravar Áudio')}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* AGC: Content */}
        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* AGC: Recording status */}
          <div className="text-center">
            {isRecording ? (
              <div className="space-y-4">
                <div className="w-24 h-24 mx-auto rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <Mic className="w-12 h-12 text-red-600 dark:text-red-400 animate-pulse" />
                </div>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  Gravando...
                </p>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400 tabular-nums">
                  {formatDuration(duration)}
                </p>
              </div>
            ) : audioBlob ? (
              <div className="space-y-4">
                <div className="w-24 h-24 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Check className="w-12 h-12 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  Gravação completa
                </p>
                <p className="text-xl font-bold text-gray-700 dark:text-gray-300 tabular-nums">
                  {formatDuration(duration)}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="w-24 h-24 mx-auto rounded-full bg-gray-100 dark:bg-slate-800 flex items-center justify-center">
                  <Mic className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  Toque para gravar
                </p>
              </div>
            )}
          </div>

          {/* AGC: Waveform visualization */}
          {isRecording && (
            <div className="flex items-center justify-center gap-1 h-16">
              {Array.from({ length: 20 }).map((_, i) => (
                <div
                  key={i}
                  className="w-1 bg-red-500 rounded-full animate-pulse"
                  style={{
                    height: `${20 + Math.random() * 80}%`,
                    animationDelay: `${i * 50}ms`,
                    animationDuration: '0.5s',
                  }}
                />
              ))}
            </div>
          )}

          {/* AGC: Playback controls */}
          {audioBlob && !isRecording && (
            <div className="flex justify-center">
              <button
                onClick={togglePlayback}
                className="p-4 rounded-full bg-green-600 hover:bg-green-700 transition-colors"
              >
                {isPlaying ? (
                  <Pause className="w-6 h-6 text-white" />
                ) : (
                  <Play className="w-6 h-6 text-white" />
                )}
              </button>
            </div>
          )}

          {/* AGC: Metadata strip — GPS, description, team tags
              AGC: Only shown after recording is complete so it doesn't distract during recording */}
          {audioBlob && !isRecording && (
            <div className="relative h-12">
              <NoteMetadataStrip
                gpsLocation={metadata.gpsLocation}
                gpsLoading={metadata.gpsLoading}
                gpsError={metadata.gpsError}
                onRetryGPS={metadata.captureGPS}
                description={metadata.description}
                onDescriptionChange={metadata.setDescription}
                isListening={metadata.isListening}
                isProcessingSpeech={metadata.isProcessingSpeech}
                onToggleSpeechToText={() => metadata.toggleSpeechToText()}
                taggedUserIds={metadata.taggedUserIds}
                onTaggedUsersChange={metadata.setTaggedUserIds}
                variant="card"
              />
            </div>
          )}
        </div>

        {/* AGC: Footer actions */}
        <div className="p-4 border-t border-gray-200 dark:border-slate-700">
          {isRecording ? (
            <button
              onClick={handleStopRecording}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium transition-colors"
            >
              <Square className="w-5 h-5" />
              Parar gravação
            </button>
          ) : audioBlob ? (
            <div className="flex gap-3">
              <button
                onClick={handleReRecord}
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Regravar
              </button>
              <button
                onClick={handleSave}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-colors"
              >
                <Check className="w-5 h-5" />
                Salvar
              </button>
            </div>
          ) : (
            <button
              onClick={handleStartRecording}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-colors"
            >
              <Mic className="w-5 h-5" />
              Iniciar gravação
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default AudioNoteModal
